import React, { useEffect, useState } from 'react';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { Alert } from '../../../CommonComponents/OPL/Alert/Alert';
import { Loading } from '../../../CommonComponents/OPL/Loading/Loading';
import { useApplyReconciliationMutation } from '../api/useApplyReconciliationMutation';
import { CATEGORIES, isActionable } from '../meta';
import {
  ApplyReconciliationResult,
  CategorySection,
  Decisions,
  DifferenceDecision,
  ReconciliationProposal,
} from '../pdfReconciliationTypes';

interface Props {
  batchId: string;
  itemId: string;
  clientId: number;
  decisions: Decisions;
  proposal: ReconciliationProposal;
  onApplied: (result: ApplyReconciliationResult) => void;
  onApplyingChange: (isApplying: boolean) => void;
  onClose: () => void;
}

interface CategoryCounts {
  added: number;
  replaced: number;
  discarded: number;
}

const summarizeSection = (section: CategorySection, decisions: Decisions): CategoryCounts => {
  const decisionOf = (row: CategorySection['rows'][number]): DifferenceDecision =>
    decisions[section.categoryKey]?.[row.rowId] || row.defaultDecision;

  const actionable = section.rows.filter(isActionable);

  const countBy = (decision: DifferenceDecision) =>
    actionable.filter(row => decisionOf(row) === decision).length;

  return {
    added: countBy('Add'),
    replaced: countBy('Overwrite'),
    discarded: countBy('Discard'),
  };
};

const summarizeDecisions = (proposal: ReconciliationProposal, decisions: Decisions) =>
  CATEGORIES.reduce<Record<string, CategoryCounts>>((summary, def) => {
    const section = proposal[def.key] as CategorySection | undefined;

    if (!section) {
      return summary;
    }

    const counts = summarizeSection(section, decisions);

    const total = counts.added + counts.replaced + counts.discarded;

    return total > 0 ? { ...summary, [def.label]: counts } : summary;
  }, {});

export const FinalizeImportStep = ({
  batchId,
  itemId,
  clientId,
  decisions,
  proposal,
  onApplied,
  onApplyingChange,
  onClose,
}: Props) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [appliedResult, setAppliedResult] = useState<ApplyReconciliationResult | null>(null);
  const { mutateAsync: apply, isLoading } = useApplyReconciliationMutation();

  useEffect(() => {
    onApplyingChange(isLoading);
  }, [isLoading, onApplyingChange]);

  const decisionSummary = summarizeDecisions(proposal, decisions);

  const handleApply = async () => {
    setErrorMessage(null);
    try {
      const result = await apply({ batchId, itemId, clientId, decisions });
      setAppliedResult(result);
      onApplied(result);
    } catch (e) {
      setErrorMessage('Failed to apply changes. Please try again.');
    }
  };

  if (appliedResult) {
    return (
      <Box>
        <Alert
          variant="success"
          title="Import complete"
          message="The household has been updated. Refreshed sections will show the merged data."
        />
        {appliedResult.warnings && appliedResult.warnings.length > 0 && (
          <Box mt="16">
            <Alert
              variant="warning"
              title="Warnings"
              message={
                <Box as="ul" m="0" pl="16" css={{ listStyleType: 'disc' }}>
                  {appliedResult.warnings.map((warning, index) => (
                    <Box
                      as="li"
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      mb={index < appliedResult.warnings.length - 1 ? '8' : '0'}
                    >
                      <Text as="span" color="inherit">
                        {warning}
                      </Text>
                    </Box>
                  ))}
                </Box>
              }
            />
          </Box>
        )}
        <Box mt="16" display="flex" justifyContent="flex-end">
          <Button onClick={onClose}>Done</Button>
        </Box>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        py="48"
        css={({ theme }) => ({ gap: theme.space[12] })}
      >
        <Loading text="Applying changes to the household…" />
      </Box>
    );
  }

  return (
    <Box>
      {errorMessage && (
        <Alert variant="error" title="Apply failed" message={errorMessage} mb="16" />
      )}

      <Text color="neutral.600" mb="16">
        Review the summary of changes before applying. You can still go back to adjust decisions.
      </Text>

      <Box>
        {Object.entries(decisionSummary).map(([category, counts]) => {
          const parts = [
            counts.added > 0 && `${counts.added} added`,
            counts.replaced > 0 && `${counts.replaced} replaced`,
            counts.discarded > 0 && `${counts.discarded} discarded`,
          ].filter(Boolean);

          return (
            <Box
              key={category}
              display="flex"
              justifyContent="space-between"
              py="8"
              borderBottom="1px solid"
              borderColor="neutral.100"
            >
              <Text fontWeight={500}>{category}</Text>

              <Text color="neutral.600" fontSize="small">
                {parts.join(' \u00b7 ')}
              </Text>
            </Box>
          );
        })}
      </Box>

      <Box mt="16" display="flex" justifyContent="flex-end">
        <Button onClick={handleApply} isLoading={isLoading}>
          Apply changes
        </Button>
      </Box>
    </Box>
  );
};
